// ============================================================================
// RISK SCORE SERVICE - Overall Player Risk Calculation
//  SECURITY FIX: Fixed bug on line 121 (odD -> userId)

// ============================================================================
// This service calculates and maintains overall risk scores for players
// based on all detection signals (multi-account, collusion, bot, dumping).
// Used for prioritizing admin review.
// ============================================================================

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  PlayerRiskScore,
  RiskHistoryEntry,
  SecurityFlag,
} from '../../types/securityTypes';
import securityFlagService from './securityFlagService';
import collusionDetectionService from './collusionDetectionService';
import botDetectionService from './botDetectionService';
import chipDumpingService from './chipDumpingService';
import deviceFingerprintService from './deviceFingerprintService';

// ============================================================================
// CONSTANTS
// ============================================================================

const RISK_SCORES_COLLECTION = 'playerRiskScores';
const MAX_HISTORY_ENTRIES = 30;

// Risk weights for different detection types
const RISK_WEIGHTS = {
  multiAccount: 0.35,      // 35% weight
  collusion: 0.30,         // 30% weight
  bot: 0.20,               // 20% weight
  chipDumping: 0.15,       // 15% weight
} as const;

// Risk level thresholds
const RISK_LEVELS = {
  low: 25,
  medium: 50,
  high: 75,
} as const;

// ============================================================================
// RISK SCORE SERVICE CLASS
// ============================================================================

class RiskScoreService {
  // ============================================================================
  // RISK CALCULATION
  // ============================================================================

  /**
   * Calculate overall risk score for a player
   * Aggregates all detection signals
   */
  async calculatePlayerRisk(userId: string): Promise<PlayerRiskScore> {
    try {
      console.log(`📊 [RiskScore] Calculating risk for ${userId.substring(0, 8)}`);

      // Get all component scores
      const [
        multiAccountRisk,
        collusionRisk,
        botRisk,
        chipDumpRisk,
        flagCounts,
      ] = await Promise.all([
        this.getMultiAccountRisk(userId),
        this.getCollusionRisk(userId),
        botDetectionService.getBotScore(userId),
        chipDumpingService.calculatePlayerDumpScore(userId),
        this.getFlagCounts(userId),
      ]);

      // Calculate weighted overall risk
      const overallRisk = Math.min(100, Math.round(
        multiAccountRisk * RISK_WEIGHTS.multiAccount +
        collusionRisk * RISK_WEIGHTS.collusion +
        botRisk * RISK_WEIGHTS.bot +
        chipDumpRisk * RISK_WEIGHTS.chipDumping
      ));

      // Determine risk level
      const riskLevel = this.getRiskLevel(overallRisk);

      console.log(`📊 [RiskScore] ${userId.substring(0, 8)}: ${overallRisk}% (${riskLevel})`);
      console.log(`   Multi-account: ${multiAccountRisk}%, Collusion: ${collusionRisk}%, Bot: ${botRisk}%, ChipDump: ${chipDumpRisk}%`);

      // Get existing record to preserve history
      const existing = await this.getPlayerRiskScore(userId);
      const riskHistory = existing?.riskHistory || [];

      // Add new history entry if score changed significantly
      if (!existing || Math.abs(existing.overallRisk - overallRisk) >= 5) {
        riskHistory.push({
          date: Timestamp.now(),
          overallRisk,
        });

        // Trim history
        while (riskHistory.length > MAX_HISTORY_ENTRIES) {
          riskHistory.shift();
        }
      }

      // Build risk score object
      //  BUG FIX: Changed 'odD: odD' to 'userId: userId'

      const riskScore: PlayerRiskScore = {
        userId: userId,  //  FIXED: Was incorrectly 'odD: odD'

        overallRisk,
        riskLevel,
        multiAccountRisk,
        collusionRisk,
        botRisk,
        chipDumpRisk,
        totalFlags: flagCounts.total,
        activeFlags: flagCounts.active,
        resolvedFlags: flagCounts.resolved,
        confirmedFlags: flagCounts.confirmed,
        lastUpdated: Timestamp.now(),
        riskHistory,
      };

      // Save to Firestore
      const riskRef = doc(db, RISK_SCORES_COLLECTION, userId);
      await setDoc(riskRef, riskScore);

      return riskScore;

    } catch (error) {
      console.error('❌ [RiskScore] Error calculating player risk:', error);
      
      // Return default risk score
      return {
        userId: userId,  //  FIXED: Was incorrectly 'odD: userId'

        overallRisk: 0,
        riskLevel: 'low',
        multiAccountRisk: 0,
        collusionRisk: 0,
        botRisk: 0,
        chipDumpRisk: 0,
        totalFlags: 0,
        activeFlags: 0,
        resolvedFlags: 0,
        confirmedFlags: 0,
        lastUpdated: Timestamp.now(),
        riskHistory: [],
      };
    }
  }

  /**
   * Get risk level from score
   */
  private getRiskLevel(score: number): 'low' | 'medium' | 'high' | 'critical' {
    if (score >= RISK_LEVELS.high) return 'critical';
    if (score >= RISK_LEVELS.medium) return 'high';
    if (score >= RISK_LEVELS.low) return 'medium';
    return 'low';
  }

  /**
   * Calculate multi-account risk based on shared devices/IPs
   */
  private async getMultiAccountRisk(userId: string): Promise<number> {
    try {
      const profile = await deviceFingerprintService.getUserSecurityProfile(userId);
      
      if (!profile) return 0;

      let risk = 0;

      // Check for multiple users on same devices
      const devices = profile.knownDevices || [];
      for (const deviceHash of devices) {
        const usersOnDevice = await deviceFingerprintService.getUsersForDevice(deviceHash);
        if (usersOnDevice.length > 1) {
          risk += Math.min(50, usersOnDevice.length * 20);
        }
      }

      // Check for active multi-account flags
      const flags = await securityFlagService.getFlags({
        userId,
        type: 'multi_account',
        status: 'new',
      });

      if (flags.length > 0) {
        risk += 30;
      }

      return Math.min(100, risk);

    } catch (error) {
      console.error('Error getting multi-account risk:', error);
      return 0;
    }
  }

  /**
   * Calculate collusion risk based on suspicious relationships
   */
  private async getCollusionRisk(userId: string): Promise<number> {
    try {
      const relationships = await collusionDetectionService.getSuspiciousRelationships(userId, 40);
      
      if (relationships.length === 0) return 0;

      // Average of top 3 suspicious relationships
      const topScores = relationships
        .map(r => r.collusionScore)
        .sort((a, b) => b - a)
        .slice(0, 3);

      const avgScore = topScores.reduce((a, b) => a + b, 0) / topScores.length;

      return Math.round(avgScore);

    } catch (error) {
      console.error('Error getting collusion risk:', error);
      return 0;
    }
  }

  /**
   * Get flag counts for a player
   */
  private async getFlagCounts(userId: string): Promise<{
    total: number;
    active: number;
    resolved: number;
    confirmed: number;
  }> {
    try {
      const flags = await securityFlagService.getPlayerFlags(userId);
      
      return {
        total: flags.length,
        active: flags.filter(f => f.status === 'new' || f.status === 'reviewing').length,
        resolved: flags.filter(f => f.status === 'resolved').length,
        confirmed: flags.filter(f => f.resolution === 'confirmed').length,
      };

    } catch (error) {
      console.error('Error getting flag counts:', error);
      return { total: 0, active: 0, resolved: 0, confirmed: 0 };
    }
  }

  // ============================================================================
  // RISK SCORE QUERIES
  // ============================================================================

  /**
   * Get player risk score
   */
  async getPlayerRiskScore(userId: string): Promise<PlayerRiskScore | null> {
    try {
      const riskRef = doc(db, RISK_SCORES_COLLECTION, userId);
      const riskDoc = await getDoc(riskRef);

      if (riskDoc.exists()) {
        return riskDoc.data() as PlayerRiskScore;
      }

      return null;

    } catch (error) {
      console.error('❌ [RiskScore] Error getting player risk:', error);
      return null;
    }
  }

  /**
   * Get or calculate player risk score
   */
  async getOrCalculateRisk(userId: string): Promise<PlayerRiskScore> {
    const existing = await this.getPlayerRiskScore(userId);
    
    // If exists and updated within last hour, return cached
    if (existing) {
      const hourAgo = Date.now() - 60 * 60 * 1000;
      if (existing.lastUpdated.toMillis() > hourAgo) {
        return existing;
      }
    }

    // Calculate fresh
    return this.calculatePlayerRisk(userId);
  }

  /**
   * Get all high-risk players
   * @param limitOrLevel - Either a number (limit) or risk level string
   * @param limitCount - Limit if first arg is risk level
   */
  async getHighRiskPlayers(
    limitOrLevel: number | 'medium' | 'high' | 'critical' = 'high',
    limitCount: number = 50
  ): Promise<PlayerRiskScore[]> {
    try {
      // Handle both call styles: getHighRiskPlayers(10) or getHighRiskPlayers('high', 10)
      let minRiskLevel: 'medium' | 'high' | 'critical';
      let actualLimit: number;
      
      if (typeof limitOrLevel === 'number') {
        minRiskLevel = 'high';
        actualLimit = limitOrLevel;
      } else {
        minRiskLevel = limitOrLevel;
        actualLimit = limitCount;
      }

      const minScore = minRiskLevel === 'critical' 
        ? RISK_LEVELS.high 
        : minRiskLevel === 'high' 
          ? RISK_LEVELS.medium 
          : RISK_LEVELS.low;

      const q = query(
        collection(db, RISK_SCORES_COLLECTION),
        where('overallRisk', '>=', minScore),
        orderBy('overallRisk', 'desc'),
        limit(actualLimit)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as PlayerRiskScore);

    } catch (error) {
      console.error('❌ [RiskScore] Error getting high risk players:', error);
      return [];
    }
  }

  /**
   * Get players by risk level
   */
  async getPlayersByRiskLevel(
    riskLevel: 'low' | 'medium' | 'high' | 'critical',
    limitCount: number = 50
  ): Promise<PlayerRiskScore[]> {
    try {
      const q = query(
        collection(db, RISK_SCORES_COLLECTION),
        where('riskLevel', '==', riskLevel),
        orderBy('overallRisk', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as PlayerRiskScore);

    } catch (error) {
      console.error('❌ [RiskScore] Error getting players by risk level:', error);
      return [];
    }
  }

  /**
   * Get risk score breakdown for admin display
   */
  async getRiskBreakdown(userId: string): Promise<{
    overall: number;
    level: string;
    components: {
      name: string;
      score: number;
      weight: number;
      contribution: number;
    }[];
  }> {
    const risk = await this.getOrCalculateRisk(userId);

    return {
      overall: risk.overallRisk,
      level: risk.riskLevel,
      components: [
        {
          name: 'Multi-Account',
          score: risk.multiAccountRisk,
          weight: RISK_WEIGHTS.multiAccount,
          contribution: Math.round(risk.multiAccountRisk * RISK_WEIGHTS.multiAccount),
        },
        {
          name: 'Collusion',
          score: risk.collusionRisk,
          weight: RISK_WEIGHTS.collusion,
          contribution: Math.round(risk.collusionRisk * RISK_WEIGHTS.collusion),
        },
        {
          name: 'Bot',
          score: risk.botRisk,
          weight: RISK_WEIGHTS.bot,
          contribution: Math.round(risk.botRisk * RISK_WEIGHTS.bot),
        },
        {
          name: 'Chip Dumping',
          score: risk.chipDumpRisk,
          weight: RISK_WEIGHTS.chipDumping,
          contribution: Math.round(risk.chipDumpRisk * RISK_WEIGHTS.chipDumping),
        },
      ],
    };
  }

  // ============================================================================
  // BATCH OPERATIONS
  // ============================================================================

  /**
   * Recalculate risk for all players with active flags
   * Run as a scheduled job
   */
  async recalculateAllRisks(): Promise<number> {
    try {
      console.log(`🔄 [RiskScore] Starting full risk recalculation`);

      // Get all players with active flags
      const flags = await securityFlagService.getFlags({
        status: 'new',
        limit: 100,
      });

      const reviewingFlags = await securityFlagService.getFlags({
        status: 'reviewing',
        limit: 100,
      });

      const allFlags = [...flags, ...reviewingFlags];

      // Get unique player IDs
      const playerIds = new Set<string>();
      for (const flag of allFlags) {
        flag.flaggedUserIds.forEach(id => playerIds.add(id));
      }

      console.log(`📊 [RiskScore] Recalculating for ${playerIds.size} players`);

      // Calculate risk for each player
      let count = 0;
      for (const userId of playerIds) {
        await this.calculatePlayerRisk(userId);
        count++;
      }

      console.log(`✅ [RiskScore] Recalculated ${count} player risks`);

      return count;

    } catch (error) {
      console.error('❌ [RiskScore] Error in batch recalculation:', error);
      return 0;
    }
  }

  /**
   * Update risk score after flag resolution
   */
  async updateRiskAfterFlagResolution(userId: string): Promise<void> {
    // Recalculate risk immediately after flag resolution
    await this.calculatePlayerRisk(userId);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get risk level color for UI
   */
  getRiskLevelColor(level: 'low' | 'medium' | 'high' | 'critical'): string {
    switch (level) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  }

  /**
   * Get risk level icon/emoji
   */
  getRiskLevelEmoji(level: 'low' | 'medium' | 'high' | 'critical'): string {
    switch (level) {
      case 'critical': return '';

      case 'high': return '';

      case 'medium': return '';

      case 'low': return '';

      default: return '';

    }
  }

  /**
   * Format risk score for display
   */
  formatRiskScore(score: number): string {
    return `${score}%`;
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

const riskScoreService = new RiskScoreService();
export { riskScoreService };
export default riskScoreService;